import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Text, Skeleton, Button } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { formatRelative, shortId } from '../../../lib/format';
import { OverviewTab } from './components/OverviewTab';
import { DefinitionTab } from './components/DefinitionTab';
import { RunsTab } from './components/RunsTab';
import { LifecycleTab } from './components/LifecycleTab';
import { FlowSummary } from './components/FlowSummary';
import { useT } from '../../../i18n/I18nProvider';
import { DetailHeader, StatusPill, BackIcon, ExternalIcon } from '../../../components/panel';
import '../flows.css';

export default function FlowViewPage() {
  const t = useT();

  // §4 item 9: uncontrolled tabs meant the Runs tab could not be linked to, so
  // the list page had to open its own duplicate drawer instead of navigating.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const setTab = (next: string) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    q.set('tab', next);
    return q;
  }, { replace: true });
  const { id } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['flow', id],
    queryFn: () => flows.getFlow(id!),
    enabled: !!id,
    // No flows hub exists, so a modest poll is the only way a pause made
    // elsewhere reaches this page.
    refetchInterval: 30_000
  });

  if (isLoading) return <FlowSkeleton />;
  if (error) return <Alert color="error" role="alert">{(error as Error).message}</Alert>;
  if (!data?.data) {
    return (
      <Alert color="warning">
        <Stack gap={8}>
          <Text role="bodySmall">{t('flows.builder.flowNotFound')}</Text>
          <Link to="/flows" className="rr-detailhead__back">
            <BackIcon /> {t('flows.view.backToAll')}
          </Link>
        </Stack>
      </Alert>
    );
  }
  const flow = data.data;

  return (
    <Stack gap="md">
      <BreadcrumbTrail
        items={[{ label: t('flows.title'), href: '/flows' }, { label: flow.name }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />

      <Card withBorder>
        <DetailHeader
          icon={<FlowIcon />}
          tint={flow.is_active ? 'var(--rr-color-success, #16a34a)' : 'var(--rr-color-text-muted)'}
          title={flow.name}
          pill={
            <StatusPill tone={flow.is_active ? 'active' : 'paused'}>
              {flow.is_active ? t('status.active') : t('status.inactive')}
            </StatusPill>
          }
          description={flow.description}
          meta={
            <>
              {/* The id is on screen so it can be quoted in a support thread,
                  which means it has to be copyable. It was selectable text. */}
              <button
                type="button"
                className="rr-copyid"
                onClick={() => {
                  navigator.clipboard.writeText(flow.id)
                    .then(() => notifySuccess(t('flows.view.idCopied'), t('flows.title')))
                    .catch(() => { /* a blocked clipboard is not worth a toast of its own */ });
                }}
              >
                {t('broadcasts.view.header.id')} <code>{shortId(flow.id)}</code>
                <CopyIcon />
              </button>
              <span aria-hidden>·</span>
              <span>{t('flows.view.updatedPrefix')} {formatRelative(flow.updated_at)}</span>
            </>
          }
          actions={
            <>
              {/* W38: visual builder over the same definition (Save = PUT /v1/flows/{id}). */}
              <Button
                size="sm"
                variant="default"
                rightSection={<ExternalIcon />}
                onClick={() => nav(`/flows/${flow.id}/builder`)}
              >
                {t('flows.builder.open')}
              </Button>
              <Menu>
                <Menu.Target>
                  <Button size="sm" variant="default" aria-label={t('common.actions')}>⋯</Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => setTab('definition')}>{t('flows.drawer.definition')}</Menu.Item>
                  <Menu.Item onClick={() => setTab('lifecycle')}>{t('broadcasts.view.tabs.lifecycle')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          }
          back={
            <Link to="/flows" className="rr-detailhead__back">
              <BackIcon /> {t('flows.view.backToAll')}
            </Link>
          }
        />
      </Card>

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<EyeIcon />}>{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="runs" leftSection={<RunsIcon />}>{t('flows.view.tabs.runs')}</Tabs.Tab>
          <Tabs.Tab value="definition" leftSection={<CodeIcon />}>{t('flows.drawer.definition')}</Tabs.Tab>
          <Tabs.Tab value="lifecycle" leftSection={<ClockIcon />}>{t('broadcasts.view.tabs.lifecycle')}</Tabs.Tab>
        </Tabs.List>

        {/*
          Overview pairs the record with what the flow actually DOES: the
          details card answered "what is this called and when did it change?"
          and nothing on the page answered "what happens when it runs?", though
          the definition was right there to be read.
        */}
        <Tabs.Panel value="overview">
          <div className="rr-flows__grid">
            <OverviewTab flow={flow} />
            <FlowSummary flow={flow} />
          </div>
          <div className="rr-flows__grid" style={{ marginTop: 16 }}>
            <RunsTab flowId={flow.id} compact />
            <LifecycleTab flow={flow} />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="runs"><div style={{ paddingTop: 16 }}><RunsTab flowId={flow.id} /></div></Tabs.Panel>
        <Tabs.Panel value="definition"><div style={{ paddingTop: 16 }}><DefinitionTab flow={flow} /></div></Tabs.Panel>
        <Tabs.Panel value="lifecycle"><div style={{ paddingTop: 16 }}><LifecycleTab flow={flow} /></div></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/** Shaped like the page it stands in for, rather than one grey block. */
function FlowSkeleton() {
  return (
    <Stack gap="md">
      <Skeleton height={18} width={200} />
      <Card withBorder>
        <div className="rr-detailhead">
          <Skeleton height={56} width={56} radius="lg" />
          <div className="rr-detailhead__main">
            <Skeleton height={24} width="40%" />
            <Skeleton height={14} width="55%" />
          </div>
        </div>
      </Card>
      <Skeleton height={38} />
      <div className="rr-flows__grid">
        <Skeleton height={280} />
        <Skeleton height={280} />
      </div>
    </Stack>
  );
}

function FlowIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="5" cy="5" r="2.3" />
      <circle cx="15" cy="10" r="2.3" />
      <circle cx="5" cy="15" r="2.3" />
      <path d="M7.3 5h2.9a2.5 2.5 0 0 1 2.5 2.5v.2M7.3 15h2.9a2.5 2.5 0 0 0 2.5-2.5v-.2" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="5.6" y="5.6" width="8" height="8" rx="1.7" />
      <path d="M10.4 5.6V3.9a1.5 1.5 0 0 0-1.5-1.5H3.9a1.5 1.5 0 0 0-1.5 1.5v5a1.5 1.5 0 0 0 1.5 1.5h1.7" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.6 8S3.9 3.8 8 3.8 14.4 8 14.4 8 12.1 12.2 8 12.2 1.6 8 1.6 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}
function RunsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />
    </svg>
  );
}
function CodeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="5.4,4.6 2,8 5.4,11.4" />
      <polyline points="10.6,4.6 14,8 10.6,11.4" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6.2" />
      <polyline points="8,4.4 8,8 10.6,9.4" />
    </svg>
  );
}
